import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  FullScreenOverlayBody,
  FullScreenOverlayHeader,
  FullScreenOverlayContent
} from '@bibliocommons/base-full-screen-overlay';
import { TableStencil } from '@bibliocommons/table';

import ListsActions from 'app/actions/ListsActions';
import Tabs from 'app/components/shared/Tabs';
import { LIST_CATEGORIES } from 'app/constants/ListsConstants';
import {
  selectDraftLists,
  selectPublishedLists,
  selectDraftListsPagination,
  selectPublishedListsPagination,
  selectDraftListsLoading,
  selectPublishedListsLoading
} from 'app/selectors/ListsSelector';
import AddToListTable from './AddToListTable';

import './AddToListOverlay.scss';

const TAB_NAMES = {
  DRAFT: 'draft',
  PUBLISHED: 'published'
};

export default function AddToListOverlay({ bibTitle, metadataId, renderBibBrief }) {
  const [selectedTab, setSelectedTab] = useState(TAB_NAMES.DRAFT);
  const dispatch = useDispatch();
  const intl = useIntl();

  const draftLists = useSelector(selectDraftLists);
  const publishedLists = useSelector(selectPublishedLists);
  const draftListsPagination = useSelector(selectDraftListsPagination);
  const publishedListsPagination = useSelector(selectPublishedListsPagination);
  const draftListsLoading = useSelector(selectDraftListsLoading);
  const publishedListsLoading = useSelector(selectPublishedListsLoading);

  const initialDraftListsLoading = draftLists.size === 0 && draftListsLoading;
  const initialPublishedListsLoading = publishedLists.size === 0 && publishedListsLoading;

  useEffect(() => {
    batch(() => {
      dispatch(ListsActions.getCurrentUserLists({ metadataId, category: LIST_CATEGORIES.DRAFT_NOT_PUBLISHED }));
      dispatch(ListsActions.getCurrentUserLists({ metadataId, category: LIST_CATEGORIES.PUBLISHED }));
    });
  }, [dispatch, metadataId]);

  function handlePageClick(page) {
    const category = selectedTab === TAB_NAMES.DRAFT ? LIST_CATEGORIES.DRAFT_NOT_PUBLISHED : LIST_CATEGORIES.PUBLISHED;
    dispatch(ListsActions.getCurrentUserLists({ metadataId, page, category }));
  }

  function renderEmptyContent() {
    return (
      <div className="empty-content">
        <FormattedMessage id={`empty_${selectedTab}_lists`} />
      </div>
    );
  }

  const counts = {
    [TAB_NAMES.DRAFT]: draftListsPagination.get('count', 0),
    [TAB_NAMES.PUBLISHED]: publishedListsPagination.get('count', 0)
  };

  const tabOptions = [
    {
      id: TAB_NAMES.DRAFT,
      label: intl.formatMessage({ id: `my_${TAB_NAMES.DRAFT}_lists` }, { count: counts[TAB_NAMES.DRAFT] }),
      renderPanelBody: () => (
        <AddToListTable
          bibTitle={bibTitle}
          handlePageClick={handlePageClick}
          renderEmptyContent={renderEmptyContent}
          lists={draftLists}
          isFetching={draftListsLoading}
          metadataId={metadataId}
          pagination={draftListsPagination}
        />
      )
    },
    {
      id: TAB_NAMES.PUBLISHED,
      label: intl.formatMessage({ id: `my_${TAB_NAMES.PUBLISHED}_lists` }, { count: counts[TAB_NAMES.PUBLISHED] }),
      renderPanelBody: () => (
        <AddToListTable
          bibTitle={bibTitle}
          handlePageClick={handlePageClick}
          renderEmptyContent={renderEmptyContent}
          lists={publishedLists}
          isFetching={publishedListsLoading}
          metadataId={metadataId}
          pagination={publishedListsPagination}
        />
      )
    }
  ];

  const onTabSelected = tabOption => {
    const { id } = tabOption;
    setSelectedTab(id);
  };

  // TODO: The BibBriefContainer works ONLY BECAUSE there is the corresponding catalogBib in state
  return (
    <FullScreenOverlayBody className="cp-add-to-list-overlay">
      <FullScreenOverlayHeader title={<FormattedMessage id="add_to_list" />} />
      <FullScreenOverlayContent>
        {/* <BibBriefContainer metadataId={metadataId} /> */}
        {renderBibBrief()}
        {initialDraftListsLoading || initialPublishedListsLoading ? (
          <TableStencil />
        ) : (
          <Tabs
            ariaLabel={intl.formatMessage({ id: 'add_to_list' })}
            options={tabOptions}
            onTabSelected={onTabSelected}
          />
        )}
      </FullScreenOverlayContent>
    </FullScreenOverlayBody>
  );
}

AddToListOverlay.propTypes = {
  bibTitle: PropTypes.string.isRequired,
  metadataId: PropTypes.string.isRequired,
  renderBibBrief: PropTypes.func.isRequired
};
